/**
 * The reservation's onboarding journey, as held by the DMS.
 *
 * When the back office sends the documents it chooses which after-arrival steps
 * this tenant has to do — the inventory (step 5) and the insurance certificate
 * (step 6) — and whether an annex travels with the lease. The DMS also records
 * how far the tenant has got, which is what lets someone close the tab after
 * signing and come back days later for the inventory.
 *
 * The tenant arrives holding the id from their emailed link (?id=…), which is
 * the key everything here is fetched by.
 */

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "constant/constant";
import { Locale } from "../i18n/locale";
import { Reservation, RESERVATION } from "./reservation";
import { getChannelLogo } from "../channelLogos";
import { StepId } from "./onboardingSteps";

export type DocumentRole = "contract" | "annex";

export interface OnboardingDocument {
  title: string;
  role: DocumentRole;
  /**
   * How long this document is, measured while the package was built. Null for
   * anything sent before page counts were recorded.
   */
  pages?: number | null;
}

export type StepState = "pending" | "completed";

export interface OnboardingStepRecord {
  state: StepState;
  completed_at?: string | null;
}

/** The reservation exactly as the API sends it, before it is shaped for the UI. */
interface ApiReservation {
  tenantName: string;
  tenantFirstName: string;
  property: string;
  propertyBadge: string;
  address: string;
  district: string;
  bookingReference: string;
  channel: string;
  photo: string;
  checkIn: string | null;
  checkOut: string | null;
  inspectionPhotoCount: number;
  maxTenantPhotos: number;
  insuranceDueDays: number;
}

export interface Onboarding {
  id: string;
  generated_contract_pdf_id: string;
  flow: "A" | "B";
  /** The API speaks "english" / "french"; the UI speaks "en" / "fr". */
  language: "english" | "french" | "dutch";
  status: "pending" | "in_progress" | "completed";
  current_step: StepId;
  steps: Partial<Record<StepId, OnboardingStepRecord>>;
  inventory_step: boolean;
  insurance_step: boolean;
  contract_signed: boolean;
  /** The emailed PDF: lease plus annexes, one file, not yet countersigned. */
  document_url: string;
  documents: OnboardingDocument[];
  identity: {
    file_name: string;
    uploaded_at: string | null;
  };
  inventory: {
    /** Photos taken at the property's last cleaning, for the tenant to review. */
    cleaning_photos: string[];
    comments: string;
    photo_urls: string[];
    signed_at: string | null;
    /** The assembled signed inventory report; "" until the step is signed. */
    pdf_url: string;
  };
  insurance: {
    file_url: string;
    file_name: string;
    uploaded_at: string | null;
  };
  reservation: ApiReservation;
}

export const localeForLanguage = (language: Onboarding["language"]): Locale =>
  language === "french" ? "fr" : "en";

/** True when an annex was sent alongside the lease — one signature covers both. */
export const hasAnnex = (onboarding: Onboarding): boolean =>
  onboarding.documents.some((document) => document.role === "annex");

// The screens want a plain calendar date; the API sends an ISO timestamp.
const toCalendarDate = (value: string | null): string =>
  value ? String(value).slice(0, 10) : "";

/**
 * Shape the API's reservation into what the screens read.
 *
 * Anything the DMS has no value for keeps the placeholder rather than rendering
 * blank — a nameless property with no dates reads as broken, and the tenant has
 * no way to tell a missing field from a broken page.
 */
export const toReservation = (api: ApiReservation): Reservation => ({
  channel: api.channel || RESERVATION.channel,
  channelLogo: getChannelLogo(api.channel) || RESERVATION.channelLogo,
  photo: api.photo || RESERVATION.photo,
  tenantName: api.tenantName || RESERVATION.tenantName,
  tenantFirstName: api.tenantFirstName || RESERVATION.tenantFirstName,
  property: api.property || RESERVATION.property,
  propertyBadge: api.propertyBadge || RESERVATION.propertyBadge,
  district: api.district || RESERVATION.district,
  address: api.address || RESERVATION.address,
  bookingReference: api.bookingReference || RESERVATION.bookingReference,
  checkIn: toCalendarDate(api.checkIn) || RESERVATION.checkIn,
  checkOut: toCalendarDate(api.checkOut) || RESERVATION.checkOut,
  inspectionPhotoCount: api.inspectionPhotoCount ?? RESERVATION.inspectionPhotoCount,
  maxTenantPhotos: api.maxTenantPhotos ?? RESERVATION.maxTenantPhotos,
  insuranceDueDays: api.insuranceDueDays ?? RESERVATION.insuranceDueDays,
});

export const fetchOnboarding = async (id: string): Promise<Onboarding | null> => {
  const response = await axios.get(
    `${base_url}/onboarding/get_onboarding?id=${encodeURIComponent(id)}`
  );
  if (response.data?.error !== false) return null;
  return response.data.onboarding as Onboarding;
};

/**
 * Tell the DMS the tenant finished a step.
 *
 * Deliberately not awaited by the navigation: a tenant who has done the work
 * should not be held on a screen because the write is slow, and the step is
 * recorded again if they revisit. Failures are logged, not surfaced.
 */
export const completeStep = async (id: string, step: StepId): Promise<void> => {
  try {
    await axios.post(`${base_url}/onboarding/complete_step`, { id, step });
  } catch (error) {
    console.error(`Failed to record onboarding step "${step}":`, error);
  }
};

/**
 * Submit the tenant's signature for the document set.
 *
 * The server rebuilds the package from the record it holds and stamps the
 * signature on it — the only thing it takes from here is the drawn image, so a
 * tampered request cannot change what was signed.
 *
 * Awaited, unlike step completion: a signature that silently failed to save
 * would leave the tenant believing they had signed a lease that nobody holds.
 */
export const submitContractSignature = async (
  generatedPdfId: string,
  signatureData: string
): Promise<boolean> => {
  try {
    const response = await axios.post(`${base_url}/contract/save-signature`, {
      signatureData,
      generatedPdfId,
    });
    return response.data?.error === false;
  } catch (error) {
    console.error("Failed to submit contract signature:", error);
    return false;
  }
};

/**
 * Save the countersigned documents to the tenant's device.
 *
 * Routed through the API rather than linking at the S3 URL directly: the API
 * streams the file back with an attachment disposition, so this is a real
 * download rather than a tab that opens the PDF, and it does not depend on the
 * bucket allowing cross-origin reads from this app.
 *
 * Only meaningful once the contract is signed — before that the stored file is
 * the unsigned copy that was emailed.
 */
export const downloadSignedDocuments = async (
  generatedPdfId: string,
  title: string
): Promise<boolean> => {
  try {
    const response = await axios.post(
      `${base_url}/contract/download-signed-contract`,
      { id: generatedPdfId, title },
      { responseType: "blob" }
    );
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${title}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error("Failed to download the signed documents:", error);
    return false;
  }
};

/** Trigger a browser "Save" for an already-fetched blob. */
const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Fetch the signed contract package as a blob (no save). */
const fetchSignedContractBlob = async (
  generatedPdfId: string,
  title: string
): Promise<Blob | null> => {
  try {
    const response = await axios.post(
      `${base_url}/contract/download-signed-contract`,
      { id: generatedPdfId, title },
      { responseType: "blob" }
    );
    return new Blob([response.data], { type: "application/pdf" });
  } catch (error) {
    console.error("Failed to fetch the signed contract:", error);
    return null;
  }
};

/** Fetch the signed inventory report as a blob (no save). */
const fetchInventoryBlob = async (
  generatedPdfId: string
): Promise<Blob | null> => {
  try {
    const response = await axios.get(
      `${base_url}/onboarding/download_inventory?generated_contract_pdf_id=${encodeURIComponent(generatedPdfId)}`,
      { responseType: "blob" }
    );
    return new Blob([response.data], { type: "application/pdf" });
  } catch (error) {
    console.error("Failed to fetch the inventory document:", error);
    return null;
  }
};

/**
 * Save every document the tenant is owed — the signed contract and, when the
 * journey produced one, the signed inventory report.
 *
 * Both files are fetched first (in parallel), then saved back-to-back in the
 * same tick. Triggering the second save only after the first had already
 * finished its own network round trip put it far enough past the click that the
 * browser dropped it as a stray download — so the inventory never arrived.
 */
export const downloadTenantDocuments = async (
  generatedPdfId: string,
  contractTitle: string,
  includeInventory: boolean
): Promise<void> => {
  const [contractBlob, inventoryBlob] = await Promise.all([
    fetchSignedContractBlob(generatedPdfId, contractTitle),
    includeInventory ? fetchInventoryBlob(generatedPdfId) : Promise.resolve(null),
  ]);

  if (contractBlob) saveBlob(contractBlob, `${contractTitle}.pdf`);
  if (inventoryBlob) saveBlob(inventoryBlob, "Inventory report.pdf");
};

/** A File as a base64 data URL, which is how this API takes uploads. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Submit the tenant's inventory: their comments, their photos and the signature
 * confirming they reviewed the property.
 *
 * Awaited, like the contract signature: a tenant who believes they have filed
 * an inventory and has not is exactly the situation the step exists to prevent.
 */
export const submitInventory = async (
  id: string,
  comments: string,
  photos: File[],
  signature: string
): Promise<{ photo_urls: string[] } | null> => {
  try {
    const encoded = await Promise.all(photos.map(toBase64));
    const response = await axios.post(`${base_url}/onboarding/submit_inventory`, {
      id,
      comments,
      photos: encoded,
      signature,
    });
    if (response.data?.error !== false) return null;
    return response.data.inventory;
  } catch (error) {
    console.error("Failed to submit the inventory:", error);
    return null;
  }
};

/**
 * Send the tenant's identity document.
 *
 * Awaited: a tenant who believes they have filed their ID and has not is
 * exactly what the step exists to prevent.
 */
export const submitIdentity = async (
  id: string,
  file: File
): Promise<{ file_name: string } | null> => {
  try {
    const encoded = await toBase64(file);
    const response = await axios.post(`${base_url}/onboarding/submit_identity`, {
      id,
      file: encoded,
      file_name: file.name,
    });
    if (response.data?.error !== false) return null;
    return response.data.identity;
  } catch (error) {
    console.error("Failed to submit the identity document:", error);
    return null;
  }
};

/**
 * Send the tenant's liability insurance certificate.
 *
 * Awaited, like the other submissions: a tenant who believes they have filed
 * their certificate and has not is precisely what the step exists to prevent.
 */
export const submitInsurance = async (
  id: string,
  file: File
): Promise<{ file_url: string; file_name: string } | null> => {
  try {
    const encoded = await toBase64(file);
    const response = await axios.post(`${base_url}/onboarding/submit_insurance`, {
      id,
      file: encoded,
      file_name: file.name,
    });
    if (response.data?.error !== false) return null;
    return response.data.insurance;
  } catch (error) {
    console.error("Failed to submit the insurance certificate:", error);
    return null;
  }
};

export interface OnboardingState {
  onboarding: Onboarding | null;
  /** True only while a fetch is actually in flight. */
  loading: boolean;
  /** Set when the id was present but the journey could not be loaded. */
  failed: boolean;
  /** Records a step with the DMS and updates the local copy. */
  markStepComplete: (step: StepId) => void;
}

/**
 * Load the journey for the id in the URL.
 *
 * With no id — a direct visit to /tenant, or a design review — this resolves to
 * no journey and the caller keeps its own defaults, so the screens stay
 * reviewable without a live reservation behind them.
 */
export const useOnboarding = (id: string | null): OnboardingState => {
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [loading, setLoading] = useState<boolean>(!!id);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) {
      setOnboarding(null);
      setLoading(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    fetchOnboarding(id)
      .then((next) => {
        if (cancelled) return;
        setOnboarding(next);
        setFailed(next === null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to fetch onboarding:", error);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const markStepComplete = useCallback(
    (step: StepId) => {
      if (!id) return;
      // Optimistic: the tenant has done the work, so the rail should show it
      // ticked immediately rather than after a round trip.
      setOnboarding((current) => (current
        ? {
          ...current,
          steps: {
            ...current.steps,
            [step]: { state: "completed", completed_at: new Date().toISOString() },
          },
        }
        : current));
      completeStep(id, step);
    },
    [id]
  );

  return { onboarding, loading, failed, markStepComplete };
};
